import { useUTLSettingsContext } from "@dexteel/mesf-core";
import {
	Button,
	Menu,
	MenuItem,
	Typography,
	makeStyles,
} from "@material-ui/core";
import { ExpandLess, ExpandMore } from "@material-ui/icons";
import { get } from "lodash-es";
import React, { useState } from "react";
import { Link, To } from "react-router-dom";

function validateBoolean(value: any) {
	if (typeof value === "boolean") {
		return value;
	}
	if (typeof value === "string") {
		value = value.toLowerCase();
		if (
			value === "true" ||
			value === "1" ||
			value === "True" ||
			value === "TRUE" ||
			value === "yes"
		) {
			return true;
		} else if (value === "false" || value === "0") {
			return false;
		}
	} else if (typeof value === "number") {
		return !!value;
	}
	return false; // Default to false for other cases
}

const useStyles = makeStyles((theme) => ({
	menuButton: {
		marginRight: theme.spacing(2),
		color: "inherit", // Assuming you want to inherit color, adjust as needed
		display: "flex",
		alignItems: "center",
		textTransform: "none", // Prevent uppercase buttons if not desired
	},
	link: {
		textDecoration: "none",
		color: "inherit",
		display: "block",
		width: "100%",
	},
	menuItemText: {
		// No specific styles needed here if MenuItem handles it
	},
	iconMargin: {
		marginLeft: theme.spacing(0.5),
	},
}));

interface MenuItemConfig {
	label: string;
	to: To;
	// permission?: string; // Not used in the current navbar structure beyond FF_LOGBOOK_ENABLED
}

interface MenuConfig {
	id: string;
	label: string;
	items: MenuItemConfig[];
	// permission?: string; // Not used in the current navbar structure
}

const menuConfigData: MenuConfig[] = [
	{
		id: "meltshop",
		label: "MELTSHOP",
		items: [
			{ label: "Heats", to: "/meltshop-heats" },
			{ label: "Heat Report", to: "/heat-process-report" },
			{ label: "Month Report", to: "/month-report" },
			{ label: "Ladle History", to: "/ladle-history" },
		],
	},
	{
		id: "eaf",
		label: "EAF",
		items: [
			{ label: "Data Entry", to: "/eaf-data-entry" },
			{ label: "Processes", to: "/eaf-processes" },
			{ label: "Processes Data Report", to: "/eaf-processes-data-report" },
		],
	},
	{
		id: "caster",
		label: "CASTER",
		items: [
			{ label: "Data Entry", to: "/caster-data-entry" },
			{ label: "Processes", to: "/caster-processes" },
			{ label: "Caster Counters", to: "/caster-counters" },
			{ label: "Failed Startup", to: "/failed-startup" },
		],
	},
	{
		id: "lab",
		label: "LAB",
		items: [
			{ label: "Heat Report", to: "/heat-report/chemistry" },
			{ label: "L3 Chemistries", to: "/lab-l3/chemistry" },
			{ label: "L3 Mechanical", to: "/lab-l3/mechanical" },
		],
	},
	{
		id: "lab-reports",
		label: "LAB REPORTS",
		items: [
			{ label: "Chemistry Lab Activity", to: "/report/mes-lab-activity" },
			{ label: "Chemistries By Date", to: "/report/mes-report-chemistry" },
			{
				label: "Mechanical Lab Activity",
				to: "/report/mes-lab-mechanical-activity",
			},
			{ label: "Mechanical By Date", to: "/report/mes-report-mechanical" },
			{ label: "Mechanical by Heat", to: "/lab-mechanical-by-heat" },
			{ label: "Melter Report", to: "/report/mes-report-melter" },
			{
				label: "Report Nuno Chemistries",
				to: "/report/mes-lab-nuno-chemistries",
			},
			{ label: "Predictor Report", to: "/report/mes-report-predictor" },
			{ label: "HRR Report", to: "/report/mes-lab-hrr" },
			{ label: "Missed Heats Report", to: "/report/mes-lab-missed-heats" },
			{
				label: "Last 20 Chemistries",
				to: "/report/mes-report-last-chemistries",
			},
			{ label: "Last EAF Samples", to: "/report/mes-lab-last-eaf-samples" },
			{ label: "Slag Samples", to: "/report/mes-lab-slag-samples" },
		],
	},
	{
		id: "delays",
		label: "DELAYS",
		items: [
			{ label: "Caster Manager", to: { pathname: "/caster-delays-manager" } },
			{ label: "Caster Report", to: { pathname: "/caster-delays-report" } },
			{ label: "Caster Pareto", to: { pathname: "/caster-delays-pareto" } },
			{ label: "EAF Manager", to: { pathname: "/eaf-delays-manager" } },
			{ label: "EAF Report", to: { pathname: "/eaf-delays-report" } },
			{ label: "EAF Pareto", to: { pathname: "/eaf-delays-pareto" } },
		],
	},
];

const logbookMenuConfig: MenuConfig = {
	id: "logbook",
	label: "LOGBOOK",
	items: [
		{ label: "Entry", to: "/logbook-entry" },
		{ label: "Report", to: "/logbook-report" },
	],
};

const CustomNavbar = () => {
	const classes = useStyles();
	const {
		state: { settings },
	} = useUTLSettingsContext();
	const [anchorElements, setAnchorElements] = useState<{
		[key: string]: HTMLElement | null;
	}>({});

	const FF_LOGBOOK_ENABLED = validateBoolean(
		get(settings, "FF_LOGBOOK_ENABLED", false),
	);

	const handleMenuOpen = (
		event: React.MouseEvent<HTMLButtonElement>,
		menuId: string,
	) => {
		setAnchorElements((prevAnchors) => ({
			...prevAnchors,
			[menuId]: event.currentTarget,
		}));
	};

	const handleMenuClose = (menuId: string) => {
		setAnchorElements((prevAnchors) => ({
			...prevAnchors,
			[menuId]: null,
		}));
	};

	return (
		<>
			<Button
				component={Link}
				to="/meltshop-dashboard"
				className={classes.menuButton}
			>
				HOME
			</Button>

			{menuConfigData.map((menu) => {
				const currentAnchorEl = anchorElements[menu.id] || null;
				const isMenuOpen = Boolean(currentAnchorEl);

				return (
					<React.Fragment key={menu.id}>
						<Button
							aria-controls={`${menu.id}-menu`}
							aria-haspopup="true"
							onClick={(event) => handleMenuOpen(event, menu.id)}
							className={classes.menuButton}
						>
							{menu.label}
							{isMenuOpen ? (
								<ExpandLess className={classes.iconMargin} />
							) : (
								<ExpandMore className={classes.iconMargin} />
							)}
						</Button>
						<Menu
							id={`${menu.id}-menu`}
							anchorEl={currentAnchorEl}
							keepMounted
							open={isMenuOpen}
							onClose={() => handleMenuClose(menu.id)}
							getContentAnchorEl={null}
							anchorOrigin={{
								vertical: "bottom",
								horizontal: "center",
							}}
							transformOrigin={{
								vertical: "top",
								horizontal: "center",
							}}
						>
							{menu.items.map((item, index) => (
								<MenuItem
									key={`${menu.id}-item-${item.label}-${index}`}
									component={Link}
									to={item.to}
									onClick={() => handleMenuClose(menu.id)}
									className={classes.link}
								>
									<Typography className={classes.menuItemText}>
										{item.label}
									</Typography>
								</MenuItem>
							))}
						</Menu>
					</React.Fragment>
				);
			})}
			<Button
				component={Link}
				to="/shift-report"
				className={classes.menuButton}
			>
				SHIFT REPORT
			</Button>

			{FF_LOGBOOK_ENABLED &&
				(() => {
					const menu = logbookMenuConfig;
					const currentAnchorEl = anchorElements[menu.id] || null;
					const isMenuOpen = Boolean(currentAnchorEl);
					return (
						<React.Fragment key={menu.id}>
							<Button
								aria-controls={`${menu.id}-menu`}
								aria-haspopup="true"
								onClick={(event) => handleMenuOpen(event, menu.id)}
								className={classes.menuButton}
							>
								{menu.label}
								{isMenuOpen ? (
									<ExpandLess className={classes.iconMargin} />
								) : (
									<ExpandMore className={classes.iconMargin} />
								)}
							</Button>
							<Menu
								id={`${menu.id}-menu`}
								anchorEl={currentAnchorEl}
								keepMounted
								open={isMenuOpen}
								onClose={() => handleMenuClose(menu.id)}
								getContentAnchorEl={null}
								anchorOrigin={{
									vertical: "bottom",
									horizontal: "center",
								}}
								transformOrigin={{
									vertical: "top",
									horizontal: "center",
								}}
							>
								{menu.items.map((item, index) => (
									<MenuItem
										key={`${menu.id}-item-${item.label}-${index}`}
										component={Link}
										to={item.to}
										onClick={() => handleMenuClose(menu.id)}
										className={classes.link}
									>
										<Typography className={classes.menuItemText}>
											{item.label}
										</Typography>
									</MenuItem>
								))}
							</Menu>
						</React.Fragment>
					);
				})()}
		</>
	);
};
export { CustomNavbar };
